// Currency detection and conversion utilities

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  country: string;
}

// Major currencies and their symbols
export const CURRENCIES: Record<string, CurrencyInfo> = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', country: 'United States' },
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', country: 'India' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', country: 'European Union' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', country: 'United Kingdom' },
  CAD: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', country: 'Canada' },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', country: 'Australia' },
  SGD: { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', country: 'Singapore' },
  AED: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', country: 'United Arab Emirates' },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', country: 'Japan' },
  // Americas
  MXN: { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', country: 'Mexico' },
  BRL: { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', country: 'Brazil' },
  ARS: { code: 'ARS', symbol: 'AR$', name: 'Argentine Peso', country: 'Argentina' },
  COP: { code: 'COP', symbol: 'CO$', name: 'Colombian Peso', country: 'Colombia' },
  // Europe (non-EUR)
  CHF: { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', country: 'Switzerland' },
  PLN: { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', country: 'Poland' },
  SEK: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', country: 'Sweden' },
  NOK: { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', country: 'Norway' },
  DKK: { code: 'DKK', symbol: 'kr', name: 'Danish Krone', country: 'Denmark' },
  TRY: { code: 'TRY', symbol: '₺', name: 'Turkish Lira', country: 'Turkey' },
  // Asia Pacific
  KRW: { code: 'KRW', symbol: '₩', name: 'South Korean Won', country: 'South Korea' },
  CNY: { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', country: 'China' },
  HKD: { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', country: 'Hong Kong' },
  NZD: { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', country: 'New Zealand' },
  IDR: { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', country: 'Indonesia' },
  MYR: { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', country: 'Malaysia' },
  THB: { code: 'THB', symbol: '฿', name: 'Thai Baht', country: 'Thailand' },
  VND: { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', country: 'Vietnam' },
  PHP: { code: 'PHP', symbol: '₱', name: 'Philippine Peso', country: 'Philippines' },
  // Middle East & Africa
  SAR: { code: 'SAR', symbol: 'SR', name: 'Saudi Riyal', country: 'Saudi Arabia' },
  ILS: { code: 'ILS', symbol: '₪', name: 'Israeli Shekel', country: 'Israel' },
  ZAR: { code: 'ZAR', symbol: 'R', name: 'South African Rand', country: 'South Africa' },
  NGN: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', country: 'Nigeria' },
  EGP: { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound', country: 'Egypt' },
};

// Payment gateway preferences by country
// Since Stripe is invite-only in India, we use Razorpay for all customers
// Razorpay supports international payments (92 currencies, accepts international cards)
export const PAYMENT_GATEWAY_BY_COUNTRY: Record<string, 'razorpay' | 'stripe'> = {
  // India - Razorpay (best UPI support, lower fees)
  'India': 'razorpay',
  'IN': 'razorpay',
  // All other countries - Razorpay (supports international payments)
  // Note: If Stripe becomes available later, you can update specific countries
  'United States': 'razorpay',
  'US': 'razorpay',
  'United Kingdom': 'razorpay',
  'UK': 'razorpay',
  'Canada': 'razorpay',
  'CA': 'razorpay',
  'Australia': 'razorpay',
  'AU': 'razorpay',
  // EUR countries - Razorpay (supports EUR and international cards)
  'Germany': 'razorpay',
  'DE': 'razorpay',
  'France': 'razorpay',
  'FR': 'razorpay',
  'Italy': 'razorpay',
  'IT': 'razorpay',
  'Spain': 'razorpay',
  'ES': 'razorpay',
  'Netherlands': 'razorpay',
  'NL': 'razorpay',
  'Belgium': 'razorpay',
  'BE': 'razorpay',
  'Austria': 'razorpay',
  'AT': 'razorpay',
  'Portugal': 'razorpay',
  'PT': 'razorpay',
  'Finland': 'razorpay',
  'FI': 'razorpay',
  'Ireland': 'razorpay',
  'IE': 'razorpay',
  'Greece': 'razorpay',
  'GR': 'razorpay',
  'Luxembourg': 'razorpay',
  'LU': 'razorpay',
  'Slovenia': 'razorpay',
  'SI': 'razorpay',
  'Cyprus': 'razorpay',
  'CY': 'razorpay',
  'Malta': 'razorpay',
  'MT': 'razorpay',
  'Slovakia': 'razorpay',
  'SK': 'razorpay',
  'Estonia': 'razorpay',
  'EE': 'razorpay',
  'Latvia': 'razorpay',
  'LV': 'razorpay',
  'Lithuania': 'razorpay',
  'LT': 'razorpay',
  'Croatia': 'razorpay',
  'HR': 'razorpay',
  // SGD (Singapore)
  'Singapore': 'razorpay',
  'SG': 'razorpay',
  // AED (UAE)
  'UAE': 'razorpay',
  'United Arab Emirates': 'razorpay',
  'AE': 'razorpay',
};

// Default currency for each country
export const DEFAULT_CURRENCY_BY_COUNTRY: Record<string, string> = {
  // Americas
  'United States': 'USD',
  'US': 'USD',
  'Canada': 'CAD',
  'CA': 'CAD',
  'Mexico': 'MXN',
  'MX': 'MXN',
  'Brazil': 'BRL',
  'BR': 'BRL',
  'Argentina': 'ARS',
  'AR': 'ARS',
  'Colombia': 'COP',
  'CO': 'COP',
  // UK
  'United Kingdom': 'GBP',
  'UK': 'GBP',
  'GB': 'GBP',
  // EUR (Euro) - European Union countries
  'Germany': 'EUR',
  'DE': 'EUR',
  'France': 'EUR',
  'FR': 'EUR',
  'Italy': 'EUR',
  'IT': 'EUR',
  'Spain': 'EUR',
  'ES': 'EUR',
  'Netherlands': 'EUR',
  'NL': 'EUR',
  'Belgium': 'EUR',
  'BE': 'EUR',
  'Austria': 'EUR',
  'AT': 'EUR',
  'Portugal': 'EUR',
  'PT': 'EUR',
  'Finland': 'EUR',
  'FI': 'EUR',
  'Ireland': 'EUR',
  'IE': 'EUR',
  'Greece': 'EUR',
  'GR': 'EUR',
  'Luxembourg': 'EUR',
  'LU': 'EUR',
  'Slovenia': 'EUR',
  'SI': 'EUR',
  'Cyprus': 'EUR',
  'CY': 'EUR',
  'Malta': 'EUR',
  'MT': 'EUR',
  'Slovakia': 'EUR',
  'SK': 'EUR',
  'Estonia': 'EUR',
  'EE': 'EUR',
  'Latvia': 'EUR',
  'LV': 'EUR',
  'Lithuania': 'EUR',
  'LT': 'EUR',
  'Croatia': 'EUR',
  'HR': 'EUR',
  // Europe (non-EUR)
  'Switzerland': 'CHF',
  'CH': 'CHF',
  'Poland': 'PLN',
  'PL': 'PLN',
  'Sweden': 'SEK',
  'SE': 'SEK',
  'Norway': 'NOK',
  'NO': 'NOK',
  'Denmark': 'DKK',
  'DK': 'DKK',
  'Turkey': 'TRY',
  'TR': 'TRY',
  // Asia Pacific
  'India': 'INR',
  'IN': 'INR',
  'Japan': 'JPY',
  'JP': 'JPY',
  'South Korea': 'KRW',
  'KR': 'KRW',
  'China': 'CNY',
  'CN': 'CNY',
  'Hong Kong': 'HKD',
  'HK': 'HKD',
  'Singapore': 'SGD',
  'SG': 'SGD',
  'Australia': 'AUD',
  'AU': 'AUD',
  'New Zealand': 'NZD',
  'NZ': 'NZD',
  'Indonesia': 'IDR',
  'ID': 'IDR',
  'Malaysia': 'MYR',
  'MY': 'MYR',
  'Thailand': 'THB',
  'TH': 'THB',
  'Vietnam': 'VND',
  'VN': 'VND',
  'Philippines': 'PHP',
  'PH': 'PHP',
  // Middle East & Africa
  'United Arab Emirates': 'AED',
  'UAE': 'AED',
  'AE': 'AED',
  'Saudi Arabia': 'SAR',
  'SA': 'SAR',
  'Israel': 'ILS',
  'IL': 'ILS',
  'South Africa': 'ZAR',
  'ZA': 'ZAR',
  'Nigeria': 'NGN',
  'NG': 'NGN',
  'Egypt': 'EGP',
  'EG': 'EGP',
  // Other / Fallback
  'Other': 'USD',
};

// Exchange rates (these should be fetched from an API in production)
// Last updated rates - in production, use a service like exchangerate-api.com
export const EXCHANGE_RATES: Record<string, number> = {
  USD: 1.0,
  // Major currencies
  INR: 83.0,    // 1 USD = 83 INR
  EUR: 0.92,    // 1 USD = 0.92 EUR
  GBP: 0.79,    // 1 USD = 0.79 GBP
  CAD: 1.35,    // 1 USD = 1.35 CAD
  AUD: 1.52,    // 1 USD = 1.52 AUD
  SGD: 1.34,    // 1 USD = 1.34 SGD
  AED: 3.67,    // 1 USD = 3.67 AED
  JPY: 150.0,   // 1 USD = 150 JPY
  // Americas
  MXN: 17.0,    // 1 USD = 17 MXN
  BRL: 4.9,     // 1 USD = 4.9 BRL
  ARS: 800.0,   // 1 USD = 800 ARS (high inflation currency)
  COP: 4000.0,  // 1 USD = 4000 COP
  // Europe (non-EUR)
  CHF: 0.88,    // 1 USD = 0.88 CHF
  PLN: 4.0,     // 1 USD = 4.0 PLN
  SEK: 10.5,    // 1 USD = 10.5 SEK
  NOK: 10.8,    // 1 USD = 10.8 NOK
  DKK: 6.9,     // 1 USD = 6.9 DKK
  TRY: 32.0,    // 1 USD = 32 TRY
  // Asia Pacific
  KRW: 1300.0,  // 1 USD = 1300 KRW
  CNY: 7.2,     // 1 USD = 7.2 CNY
  HKD: 7.8,     // 1 USD = 7.8 HKD
  NZD: 1.65,    // 1 USD = 1.65 NZD
  IDR: 15500.0, // 1 USD = 15500 IDR
  MYR: 4.7,     // 1 USD = 4.7 MYR
  THB: 35.0,    // 1 USD = 35 THB
  VND: 24500.0, // 1 USD = 24500 VND
  PHP: 56.0,    // 1 USD = 56 PHP
  // Middle East & Africa
  SAR: 3.75,    // 1 USD = 3.75 SAR
  ILS: 3.7,     // 1 USD = 3.7 ILS
  ZAR: 19.0,    // 1 USD = 19 ZAR
  NGN: 1500.0,  // 1 USD = 1500 NGN
  EGP: 49.0,    // 1 USD = 49 EGP
};

/**
 * Normalize country names and codes to full names used in the app
 */
export const normalizeCountryName = (country: string | null): string | null => {
  if (!country) return null;

  const countryCodeMap: Record<string, string> = {
    'IN': 'India',
    'IND': 'India',
    'US': 'United States',
    'USA': 'United States',
    'GB': 'United Kingdom',
    'UK': 'United Kingdom',
    'CA': 'Canada',
    'AU': 'Australia',
    'DE': 'Germany',
    'FR': 'France',
    'IT': 'Italy',
    'ES': 'Spain',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'AT': 'Austria',
    'PT': 'Portugal',
    'FI': 'Finland',
    'IE': 'Ireland',
    'GR': 'Greece',
    'LU': 'Luxembourg',
    'SI': 'Slovenia',
    'CY': 'Cyprus',
    'MT': 'Malta',
    'SK': 'Slovakia',
    'EE': 'Estonia',
    'LV': 'Latvia',
    'LT': 'Lithuania',
    'HR': 'Croatia',
    'SG': 'Singapore',
    'AE': 'United Arab Emirates',
    'UAE': 'United Arab Emirates',
    'JP': 'Japan',
  };

  const upperResult = country.toUpperCase();
  if (countryCodeMap[upperResult]) {
    return countryCodeMap[upperResult];
  }

  // If it's already a full name in our system, return it
  return country;
};

/**
 * Detect user's country from browser/API
 * Uses multiple fallback methods: Backend IP detection, browser locale, timezone
 */
export const detectUserCountry = async (): Promise<string | null> => {
  // Check sessionStorage first (for manual override or cached value)
  try {
    const cachedCountry = sessionStorage.getItem('detectedCountry');
    if (cachedCountry) {
      console.log('🌍 Using cached country from sessionStorage:', cachedCountry);
      return normalizeCountryName(cachedCountry);
    }
  } catch (e) {
    // Ignore sessionStorage errors
  }

  // PRIORITY 1: Try backend IP detection (Firebase Function - no CORS issues)
  try {
    // Determine the URL based on the environment
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const backendUrl = isLocal
      ? 'http://localhost:5001/formulabot-1d12b/us-central1/detectUserCountry'
      : 'https://us-central1-formulabot-1d12b.cloudfunctions.net/detectUserCountry';

    console.log(`🌍 Attempting country detection via: ${isLocal ? 'Local Emulator' : 'Cloud Function'}`);

    const response = await fetch(backendUrl);
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.country) {
        console.log('🌍 Detected country via backend IP detection:', data.country);
        const normalized = normalizeCountryName(data.country);
        // Cache the result
        try {
          if (normalized) sessionStorage.setItem('detectedCountry', normalized);
        } catch (e) {
          // Ignore sessionStorage errors
        }
        return normalized;
      }
    }
  } catch (error) {
    console.warn('Backend IP detection failed:', error);
  }

  // PRIORITY 2: Try client-side IP API as fallback (may have CORS issues)
  try {
    // Use ip-api.com which sometimes works client-side
    const response = await fetch('https://ip-api.com/json/?fields=status,country,countryCode');
    if (response.ok) {
      const data = await response.json();
      if (data.status === 'success' && data.country) {
        console.log('🌍 Detected country via client-side IP API:', data.country);
        const normalized = normalizeCountryName(data.country);
        // Cache the result
        try {
          if (normalized) sessionStorage.setItem('detectedCountry', normalized);
        } catch (e) {
          // Ignore sessionStorage errors
        }
        return normalized;
      }
    }
  } catch (error) {
    console.warn('Client-side IP API detection failed (CORS expected):', error);
  }

  // Fallback: Try browser locale detection
  try {
    const locale = navigator.language || (navigator as any).userLanguage;
    if (locale) {
      // Extract country code from locale (e.g., "en-IN" -> "IN")
      const countryCode = locale.split('-')[1];
      if (countryCode) {
        const normalized = normalizeCountryName(countryCode);
        if (normalized) {
          console.log('🌍 Detected country via browser locale:', normalized, 'from locale:', locale);
          return normalized;
        }
      }
    }
  } catch (error) {
    console.warn('Browser locale detection failed:', error);
  }

  // Fallback: Try timezone-based detection
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Common timezone to country mappings
    const timezoneMap: Record<string, string> = {
      'Asia/Kolkata': 'India',
      'Asia/Calcutta': 'India',
      'America/New_York': 'United States',
      'America/Los_Angeles': 'United States',
      'America/Chicago': 'United States',
      'Europe/London': 'United Kingdom',
      'America/Toronto': 'Canada',
      'Australia/Sydney': 'Australia',
      'Europe/Berlin': 'Germany',
      'Europe/Paris': 'France',
      'Europe/Rome': 'Italy',
      'Europe/Madrid': 'Spain',
      'Europe/Amsterdam': 'Netherlands',
      'Asia/Singapore': 'Singapore',
      'Asia/Dubai': 'United Arab Emirates',
      'Asia/Tokyo': 'Japan',
    };

    if (timezone && timezoneMap[timezone]) {
      console.log('🌍 Detected country via timezone:', timezoneMap[timezone], 'from timezone:', timezone);
      return normalizeCountryName(timezoneMap[timezone]);
    }
  } catch (error) {
    console.warn('Timezone detection failed:', error);
  }

  console.warn('🌍 All country detection methods failed, returning null');
  return null;
};

/**
 * Get currency based on country
 * Handles both country names (e.g., "India") and country codes (e.g., "IN")
 */
export const getCurrencyByCountry = (country: string | null): string => {
  // Unified pricing: Always return USD
  return 'USD';
};

/**
 * Get payment gateway preference for country
 * Defaults to Razorpay since it supports international payments
 * (Stripe is invite-only in India)
 */
export const getPaymentGatewayByCountry = (country: string | null): 'razorpay' | 'stripe' => {
  if (!country) return 'razorpay'; // Default to Razorpay (supports international)

  const gateway = PAYMENT_GATEWAY_BY_COUNTRY[country] || PAYMENT_GATEWAY_BY_COUNTRY[country.toUpperCase()];
  return gateway || 'razorpay'; // Default to Razorpay if country not found
};

/**
 * Regional pricing multipliers by country
 * These represent the percentage of USD price to charge (e.g., 0.6 = 60% of USD price)
 * Set to 1.0 for no regional discount, or lower value for discounted regional pricing
 */
export const REGIONAL_PRICING_MULTIPLIERS: Record<string, number> = {
  // Removed regional discounts to unify pricing across all regions.
  // All multipliers are now effectively 1.0.
};

/**
 * Get regional pricing multiplier for a country
 * Returns 1.0 (no discount) if country not found or no regional pricing configured
 */
export const getRegionalPricingMultiplier = (country: string | null): number => {
  // Unified pricing: Always return 1.0 (no multiplier) regardless of country
  return 1.0;
};

/**
 * Convert amount from USD to target currency with regional pricing support
 * For countries with regional pricing (like India), applies the regional multiplier
 * before currency conversion to provide more affordable pricing
 * 
 * @param amountUSD - Price in USD
 * @param targetCurrency - Target currency code (e.g., 'INR', 'USD')
 * @param userCountry - User's country (optional, for regional pricing)
 * @returns Price in target currency with regional pricing applied
 */
export const convertCurrency = (
  amountUSD: number,
  targetCurrency: string,
  userCountry?: string | null
): number => {
  // Unified pricing: Always return input amount (already in USD)
  return amountUSD;
};

/**
 * Convert amount from USD to target currency (backwards compatible version without regional pricing)
 * @deprecated Use convertCurrency with userCountry parameter for regional pricing support
 */
export const convertCurrencyLegacy = (amountUSD: number, targetCurrency: string): number => {
  return convertCurrency(amountUSD, targetCurrency);
};

/**
 * Format currency amount with symbol
 * For INR, uses Indian number formatting (lakhs/crores) and proper symbol placement
 */
export const formatCurrency = (amount: number, currency: string): string => {
  const currencyInfo = CURRENCIES[currency.toUpperCase()];
  const symbol = currencyInfo?.symbol || currency.toUpperCase();

  // For INR, use Indian locale for number formatting
  if (currency.toUpperCase() === 'INR') {
    const formattedAmount = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      style: 'currency',
      currency: 'INR',
    }).format(amount);
    // Remove INR code and use symbol
    return formattedAmount.replace('INR', '₹').trim();
  }

  // For other currencies, format with proper decimals
  const formattedAmount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  // For most currencies, symbol comes before
  return `${symbol}${formattedAmount}`;
};

/**
 * Get currency info
 */
export const getCurrencyInfo = (currency: string): CurrencyInfo | null => {
  return CURRENCIES[currency.toUpperCase()] || null;
};

